import type { EntityPluginContext } from "@brains/plugins";
import { fetchStyleGuide, formatVoiceGuidance } from "@brains/contracts";
import { CallbackProgressReporter } from "@brains/utils/progress";
import { z } from "@brains/utils/zod";
import { GenerationJobHandler } from "../handlers/generationHandler";

const generationInputSchema = z.object({
  prompt: z.string().optional(),
  content: z.string().optional(),
  platform: z.enum(["linkedin"]).default("linkedin"),
});

const createInputSchema = z.object({
  prompt: z.string().optional(),
  content: z.string().optional(),
  title: z.string().optional(),
  platform: z.enum(["linkedin"]).optional(),
});

type GenerationInput = z.output<typeof generationInputSchema>;
type CreateInput = z.output<typeof createInputSchema>;

export function registerEvalHandlers(context: EntityPluginContext): void {
  // Eval: test AI text generation only (fast, no entity persistence)
  context.eval.registerHandler("generation", async (input: unknown) => {
    const parsed: GenerationInput = generationInputSchema.parse(input);

    const generationPrompt = parsed.content
      ? `Write a LinkedIn post to share this content:\n\n${parsed.content}`
      : (parsed.prompt ?? "Write a LinkedIn post");

    const voiceGuidance = formatVoiceGuidance(
      await fetchStyleGuide(context.entityService),
    );
    return context.ai.generate(
      {
        prompt: generationPrompt,
        templateName: `social-media:${parsed.platform}`,
        representedIdentity: "anchor",
        ...(voiceGuidance && { styleGuide: { voice: voiceGuidance } }),
      },
      z.object({ content: z.string() }),
    );
  });

  // Eval: run the full generation pipeline and verify entity persistence
  context.eval.registerHandler("create", async (input: unknown) => {
    const parsed: CreateInput = createInputSchema.parse(input);

    const progressSteps: Array<{ progress: number; message?: string }> = [];
    const reporter = CallbackProgressReporter.from(async (n) => {
      const step: { progress: number; message?: string } = {
        progress: n.progress,
      };
      if (n.message !== undefined) step.message = n.message;
      progressSteps.push(step);
    });
    if (!reporter) throw new Error("Failed to create progress reporter");

    const handler = new GenerationJobHandler(context.logger, context);
    const result = await handler.process(
      parsed,
      `eval-${Date.now()}`,
      reporter,
    );

    // Verify entity was actually persisted when the handler reports success
    let entityExists = false;
    let entityPreview: string | undefined;
    if (result.success && result.entityId) {
      const entity = await context.entityService.getEntity({
        entityType: "social-post",
        id: result.entityId,
      });
      entityExists = !!entity;
      entityPreview = entity?.content.slice(0, 300);
    }

    return {
      ...result,
      entityExists,
      entityPreview,
      progressSteps,
    };
  });
}
